import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { Edit, ArrowBack } from '@mui/icons-material';
import { Member } from '../../types';
import api from '../../config/api';
import { format } from 'date-fns';

export const MemberView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchMember();
    }
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/members/${id}`);
      setMember(response.data);
    } catch (error: any) {
      console.error('Error fetching member:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to load member data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'suspended':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/members')}
          variant="outlined"
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  if (!member) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Member not found
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/members')}
          variant="outlined"
        >
          Back to Members
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Member Details</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/members')}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/members/${id}/edit`)}
          >
            Edit Member
          </Button>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="textSecondary">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Member Code
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {member.member_code}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {member.first_name} {member.last_name}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {member.email}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {member.phone || '-'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="textSecondary">
                  Membership Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {member.company || '-'}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Position
                  </Typography>
                  <Typography variant="body1">
                    {member.position || '-'}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Join Date
                  </Typography>
                  <Typography variant="body1">
                    {member.join_date ? format(new Date(member.join_date), 'dd/MM/yyyy') : '-'}
                  </Typography>
                </Box>
                <Box mb={2}>
                  <Typography variant="caption" color="textSecondary">
                    Status
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={member.membership_status}
                      color={getStatusColor(member.membership_status) as any}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {member.notes && (
            <Box sx={{ flex: '1 1 100%' }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="textSecondary">
                    Notes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {member.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          <Box sx={{ flex: '1 1 100%', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/members')}
            >
              Back to List
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(`/members/${id}/edit`)}
            >
              Edit Member
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

